import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { UserService } from 'src/app/shared/services/user-services/user.service';

type ResType = 'manpower' | 'machine' | 'material' | 'stock' | '';

@Component({
  selector: 'app-vendor-resources-hub',
  templateUrl: './vendor-resources-hub.component.html',
  styleUrls: ['./vendor-resources-hub.component.css'],
})
export class VendorResourcesHubComponent implements OnInit {
  vendorId: number | null = null;
  companyName = '';
  availabilityStatus = 'available';
  filterType: ResType = '';
  rows: any[] = [];
  loading = false;

  form: FormGroup;
  readonly types: { id: ResType; label: string }[] = [
    { id: 'manpower', label: 'Manpower' },
    { id: 'machine', label: 'Machine' },
    { id: 'material', label: 'Material' },
    { id: 'stock', label: 'Stock' },
  ];

  constructor(
    private fb: FormBuilder,
    private readonly api: ApiService,
    private readonly toastr: ToastrMsgService,
    private readonly userService: UserService
  ) {
    this.form = this.fb.group({
      resource_type: ['manpower', Validators.required],
      title: ['', Validators.required],
      quantity: [null as number | null],
      unit: [''],
      stock_quantity: [null as number | null],
      availability: ['available'],
    });
  }

  ngOnInit(): void {
    const session = this.userService.getUserDetailsFromSession();
    const u = session?.user || session;
    this.vendorId = u?.vendor_company_id != null ? Number(u.vendor_company_id) : null;
    this.companyName = u?.vendor_company_name || '';
    if (!this.vendorId) {
      this.toastr.error('Vendor account not linked to this user');
      return;
    }
    this.loadVendorMeta();
    this.loadResources();
  }

  loadVendorMeta(): void {
    if (!this.vendorId) return;
    this.api.getVendorAccount(this.vendorId).subscribe({
      next: (res: any) => {
        const d = res?.data;
        if (d) {
          this.companyName = d.company_name || this.companyName;
          this.availabilityStatus = d.availability_status || 'available';
        }
      },
      error: () => {},
    });
  }

  loadResources(): void {
    if (!this.vendorId) return;
    this.loading = true;
    const type = this.filterType || undefined;
    this.api.getVendorResources(this.vendorId, type).subscribe({
      next: (res: any) => {
        this.rows = res?.data || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.toastr.error(err?.error?.message || 'Failed to load resources');
      },
    });
  }

  setFilter(t: ResType): void {
    this.filterType = t;
    this.loadResources();
  }

  submitRow(): void {
    if (!this.vendorId || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.api.createVendorResource(this.vendorId, this.form.value).subscribe({
      next: () => {
        this.toastr.success('Resource saved');
        this.form.patchValue({
          title: '',
          quantity: null,
          unit: '',
          stock_quantity: null,
        });
        this.loadResources();
      },
      error: (err) => this.toastr.error(err?.error?.message || 'Save failed'),
    });
  }

  deleteRow(r: any): void {
    if (!this.vendorId || !r?.id) return;
    if (!confirm('Delete this row?')) return;
    this.api.deleteVendorResource(this.vendorId, r.id).subscribe({
      next: () => {
        this.toastr.success('Deleted');
        this.loadResources();
      },
      error: (err) => this.toastr.error(err?.error?.message || 'Delete failed'),
    });
  }

  saveAvailability(): void {
    if (!this.vendorId) return;
    this.api.patchVendorAccount(this.vendorId, { availability_status: this.availabilityStatus }).subscribe({
      next: () => this.toastr.success('Availability updated'),
      error: (err) => this.toastr.error(err?.error?.message || 'Update failed'),
    });
  }
}
